import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../Sidebar/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faStar, faCalendarCheck, faUtensils } from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../../config/api.config';

const Performance = ({ showAlert }) => {
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  useEffect(() => {
    fetchPerformance();
  }, [selectedMonth, selectedYear]);

  const fetchPerformance = async () => {
    try {
      setLoading(true);
      const restaurantId = localStorage.getItem('currentRestaurantId');
      const token = localStorage.getItem('token');

      const res = await axios.post(
        buildApiUrl(`hr/performance/overview?month=${selectedMonth}&year=${selectedYear}`),
        { restaurantId },
        { headers: { 'auth-token': token } }
      );

      setPerformanceData(res.data.staff || []);
    } catch (error) {
      console.error("Error fetching performance:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'Excellent': return '#22c55e';
      case 'Good': return '#3b82f6';
      case 'Average': return '#f59e0b';
      case 'Needs Improvement': return '#f97316';
      default: return '#ef4444';
    }
  };

  const getRatingEmoji = (rating) => {
    switch (rating) {
      case 'Excellent': return '🌟';
      case 'Good': return '👍';
      case 'Average': return '📊';
      case 'Needs Improvement': return '⚠️';
      default: return '❌';
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0a0a0c', color: 'white' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '800', margin: 0 }}>
            <FontAwesomeIcon icon={faChartLine} style={{ marginRight: '1rem', color: '#8b5cf6' }} />
            Performance
          </h1>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <select 
              value={selectedMonth} 
              onChange={e => setSelectedMonth(parseInt(e.target.value))}
              style={{ padding: '0.8rem', background: '#1c1c1f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
            >
              {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select 
              value={selectedYear} 
              onChange={e => setSelectedYear(parseInt(e.target.value))}
              style={{ padding: '0.8rem', background: '#1c1c1f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
            >
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.5)' }}>Loading...</div>
        ) : performanceData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.5)' }}>No performance data available.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {performanceData.map(item => (
              <div key={item.staff._id} style={{ 
                background: '#1c1c1f', 
                padding: '1.5rem', 
                borderRadius: '16px',
                border: '1px solid rgba(255,255,255,0.05)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Rating Badge */}
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  padding: '0.4rem 0.8rem',
                  borderRadius: '20px',
                  background: `${getRatingColor(item.rating)}20`,
                  color: getRatingColor(item.rating),
                  fontSize: '0.75rem',
                  fontWeight: '700'
                }}>
                  {getRatingEmoji(item.rating)} {item.rating}
                </div>

                {/* Staff Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                  {item.staff.profilePhoto ? (
                    <img src={item.staff.profilePhoto} alt="" style={{ width: '55px', height: '55px', borderRadius: '12px', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '55px', height: '55px', borderRadius: '12px', background: 'rgba(139, 92, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8b5cf6', fontWeight: 'bold', fontSize: '1.3rem' }}>
                      {item.staff.name?.charAt(0)}
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>{item.staff.name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', textTransform: 'capitalize' }}>{item.staff.staffRole}</div>
                  </div>
                </div>

                {/* Attendance Ring */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ 
                    width: '120px', 
                    height: '120px', 
                    borderRadius: '50%', 
                    background: `conic-gradient(${getRatingColor(item.rating)} ${item.attendanceRate * 3.6}deg, rgba(255,255,255,0.05) 0deg)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      width: '90px', 
                      height: '90px', 
                      borderRadius: '50%', 
                      background: '#1c1c1f',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{ fontSize: '1.8rem', fontWeight: '800', color: getRatingColor(item.rating) }}>{item.attendanceRate}%</div>
                      <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>Attendance</div>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '10px', textAlign: 'center' }}>
                    <FontAwesomeIcon icon={faCalendarCheck} style={{ color: '#22c55e', marginBottom: '0.5rem' }} />
                    <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>{item.presentDays}</div>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>Days Present</div>
                  </div>
                  <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '10px', textAlign: 'center' }}>
                    <FontAwesomeIcon icon={faUtensils} style={{ color: '#f59e0b', marginBottom: '0.5rem' }} />
                    <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>{item.totalDays}</div>
                    <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>Total Days</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Performance;
